import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import { Edit2, Trash } from 'react-feather';
import { UIDFork } from 'react-uid';
import { Business, Offer } from '../../../backend/db/all-entities';
import { BusinessJwt } from '../../../misc-types/jwt-payload';
import { useBlur, useDelayedVisibility, useErrorNotifier } from '../../../utils/hooks';
import transformImageUrl from '../../../utils/image-transformation';
import request from '../../../utils/make-api-request';
import BusinessProfileHeader from '../../business/profile-header';
import { CoreUIContext } from '../../core-ui/provider';
import { UserContext } from '../../user-provider';
import { showOfferEditor } from '../editor';

type OfferProps = 'id' | 'name' | 'description' | 'price' | 'imageUrl';
export type CardRenderableOffer = Pick<Offer, OfferProps> & {
  offerer: Pick<Business, 'id' | 'name' | 'logoUrl' | 'pricingPlan' | 'geolocations' | 'currentSpecial'>;
};
interface Props {
  offer: CardRenderableOffer;
  /**
   * Called after the offer has been edited and the edit request succeeds.
   */
  onEdit?: () => void;
  /**
   * Called after the offer has been deleted from the server.
   */
  onDelete?: () => void;
}

// const OfferCardDropdown = dynamic(() => import('./dropdown'), { ssr: false });
const OfferViewer = dynamic(() => import('../viewer'), { ssr: false });

const OfferCard: React.FC<Props> = ({ offer, onEdit, onDelete }) => {
  const {
    mounted: detailsViewerMounted,
    visible: detailsViewerVisible,
    show: showDetailsViewer,
    hide: hideDetailsViewer
  } = useDelayedVisibility(200, true);
  const { jwt } = useContext(UserContext) as { jwt: BusinessJwt };
  const { showDialog } = useContext(CoreUIContext);
  const errorNotifier = useErrorNotifier();
  const { ref: detailsViewerRef, openerRef: detailsViewerOpenerRef } = useBlur<HTMLDivElement>(hideDetailsViewer);
  async function editOffer() {
    const business = (await request(`businesses/${jwt.sub}`, 'get')) as Business;
    showOfferEditor(
      showDialog,
      offer.offerer.pricingPlan,
      business.geolocations.map(g => g.point),
      offer
    )
      .then(editedOffer => request(`offers/${offer.id}`, 'patch', { offer: editedOffer }))
      .then(() => onEdit?.())
      .catch(errorNotifier);
  }
  async function deleteOffer() {
    await request(`offers/${offer.id}`, 'delete').then(() => onDelete?.(), errorNotifier);
  }
  return (
    <article className='bg-gray-700 rounded-lg'>
      {offer.imageUrl != null && (
        <img
          src={transformImageUrl(offer.imageUrl, { width: 500, height: 250 })}
          alt='Offer'
          className='w-full h-40 object-cover rounded-t-lg'
        />
      )}
      <div className='flex items-center justify-between font-medium p-4'>
        <span className='font-bold'>{offer.name}</span>
        <div
          className='text-sm bg-gray-600 p-1 px-2 text-white leading-none self-start rounded-full whitespace-no-wrap select-none'
          title={`${offer.price} USD`}
        >
          ${offer.price}
        </div>
      </div>
      <div className={classNames('relative', offer.imageUrl != null ? 'h-24' : 'h-64')}>
        <p className='px-4 flex-grow'>{offer.description}</p>
        <button
          className='tertiary view-details absolute right-0 mr-4 bottom-0'
          onClick={showDetailsViewer}
          ref={detailsViewerOpenerRef}
        >
          View
        </button>
      </div>
      <div className='flex items-center text-sm h-12 px-4'>
        <BusinessProfileHeader business={offer.offerer} />
        <div className='ml-auto flex items-center'>
          {offer.offerer.currentSpecial && (
            <img
              src='/images/shield.jpg'
              alt='Shield'
              title='This business is offering a special'
              width='32'
              height='32'
            />
          )}
          {jwt?.role === 'business' && jwt?.sub === offer.offerer.id && (
            <>
              <button
                className='hover:text-gray-400 active:text-gray-400 rounded-full ease-in-out duration-150'
                onClick={editOffer}
                aria-label='Edit'
              >
                <Edit2 size={18} />
              </button>
              <button
                className='ml-1 hover:text-gray-400 active:text-gray-400 rounded-full ease-in-out duration-150'
                onClick={deleteOffer}
                aria-label='Delete'
              >
                <Trash size={18} />
              </button>
            </>
          )}
          {/* <UIDFork>
            <OfferCardDropdown {...{ offer, onEdit, onDelete }} />
          </UIDFork> */}
        </div>
      </div>
      <div aria-live='polite'>
        {detailsViewerMounted && (
          <UIDFork>
            <OfferViewer
              offer={offer}
              visible={detailsViewerVisible}
              forwardedRef={detailsViewerRef}
              close={hideDetailsViewer}
            />
          </UIDFork>
        )}
      </div>

      <style jsx>{`
        h4 {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          text-overflow: ellipsis;
          overflow: hidden;
          word-wrap: break-word;
        }
        p {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${offer.imageUrl != null ? 4 : 10};
          text-overflow: ellipsis;
          overflow: hidden;
        }
        a.view-details {
          @apply bg-gray-700;
          box-shadow: -8px 0 4px -0px #374151, -16px 0 4px -0px #374151bb, -24px 0 4px -0px #37415177,
            -32px 0 4px -0px #37415133;
        }
      `}</style>
    </article>
  );
};

export default OfferCard;
